// §4.1 Planning workbench (`/planning`): the facility's open demand, the MPS
// status tile, the Refresh demand button with its last run and pending
// request, the refresh history, and the lines the latest refresh skipped or
// deferred (D-354 ruling 2). From P3-H-2 the MRP half follows, mounted only for
// a holder of `planning.planned_order.read` (D-387 ruling 5), and from P3-I-2
// the schedule half, mounted only for a holder of `planning.schedule.read`.
// From P3-J J-3 the promises tile follows, for every `planning.read` holder.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { StatStrip, StatTile } from "../../../../components/stat-strip";
import { EmptyState, ErrorState } from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import {
  OrderLabel,
  QtyWithUnit,
  RefreshStatusChip,
  TriggerChip,
  totalsByUnit,
  useDemandRefreshControl,
} from "../components/demand-bits";
import { MpsTile } from "../components/mps-bits";
import { MrpWorkbenchSection } from "../components/mrp-workbench";
import { ScheduleWorkbenchSection } from "../components/schedule-workbench";
import { PromisesWorkbenchSection } from "../components/promise-workbench";
import {
  useDemandEntries,
  useDemandRefresh,
  useDemandRefreshes,
  type DemandPendingRequest,
  type DemandRefresh,
  type DemandRefreshDetail,
} from "../hooks/demand-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { formatRunDuration } from "../lib/mrp-lib";
import { useOffline } from "../lib/planning-lib";
import { useProductIndex, useUoms } from "../hooks/planning-queries";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";

/** Seconds between the start and the end of a run, "1 min 12 s". */
export function runDuration(run: DemandRefresh): { minutes: number; seconds: number } | undefined {
  if (run.finished_at === null) return undefined;
  const ms = Date.parse(run.finished_at) - Date.parse(run.started_at);
  if (!Number.isFinite(ms) || ms < 0) return undefined;
  const total = Math.round(ms / 1000);
  return { minutes: Math.floor(total / 60), seconds: total % 60 };
}

export function PlanningWorkbenchScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canRefresh = perms.has("planning.demand.manage");
  const zone = facility.timezone;

  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const page = cursors.length - 1;
  const history = useDemandRefreshes(facility.id, cursors[page]);
  const head = useDemandRefreshes(facility.id, undefined);
  const pool = useDemandEntries(facility.id, undefined, {}, undefined, { limit: 1 });
  const latest = pool.data?.refresh ?? null;
  const detail = useDemandRefresh(latest?.id);
  const control = useDemandRefreshControl(facility.id, zone, { canRefresh });
  const offline = useOffline();

  useEffect(() => {
    setCursors([undefined]);
  }, [facility.id]);

  const authError = [history.error, head.error, pool.error].find(
    (e) => e instanceof UnauthorizedError,
  );
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const time = (iso: string | null) => formatTimestamp(iso, i18n.language, zone);

  const pageHead = (
    <PlanningHeader title={t("planning.workbench.title")} actions={control.button} />
  );

  const readError = head.error ?? pool.error;
  if (readError !== null && authError === undefined) {
    return (
      <section className="planning-screen" data-testid="planning-workbench">
        {pageHead}
        <ErrorState
          title={t("planning.workbench.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => {
            void head.refetch();
            void pool.refetch();
          }}
          requestId={requestIdOf(readError)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  const headData = head.data;
  const last = headData?.items[0];
  const failures = headData?.consecutive_failures ?? 0;
  const neverRun =
    headData?.items.length === 0 && headData.pending_request === null && !control.running;

  const columns: DataTableColumn<DemandRefresh>[] = [
    {
      key: "started",
      header: t("planning.workbench.columns.started"),
      id: true,
      render: (row) => time(row.started_at),
    },
    {
      key: "trigger",
      header: t("planning.workbench.columns.trigger"),
      render: (row) => <TriggerChip trigger={row.trigger} />,
    },
    {
      key: "status",
      header: t("planning.workbench.columns.status"),
      render: (row) => <RefreshStatusChip status={row.status} />,
    },
    {
      key: "duration",
      header: t("planning.workbench.columns.duration"),
      numeric: true,
      render: (row) => {
        const d = runDuration(row);
        return d === undefined ? "" : formatRunDuration(t, d);
      },
    },
    {
      key: "lines",
      header: t("planning.workbench.columns.orderLines"),
      numeric: true,
      render: (row) => row.stats.order_lines ?? "",
    },
    {
      key: "entries",
      header: t("planning.workbench.columns.entries"),
      numeric: true,
      render: (row) => row.stats.entries_written ?? "",
    },
    {
      key: "deferred",
      header: t("planning.workbench.columns.deferred"),
      numeric: true,
      render: (row) => row.stats.credit_deferred?.count ?? "",
    },
    {
      key: "skipped",
      header: t("planning.workbench.columns.skipped"),
      numeric: true,
      render: (row) => {
        const n = row.stats.skipped_count ?? 0;
        return n > 0 ? <span className="planning-bad">{n}</span> : n;
      },
    },
    {
      key: "error",
      header: t("planning.workbench.columns.error"),
      render: (row) =>
        row.status === "failed" ? (
          <span className="planning-refresh-failed">
            <span className="planning-bad" data-testid="planning-refresh-error">
              {row.error ?? "-"}
            </span>
            {canRefresh ? (
              <Button
                size="sm"
                data-testid="planning-refresh-rerun"
                disabled={control.running || offline}
                onClick={control.start}
              >
                {t("planning.workbench.rerun")}
              </Button>
            ) : null}
          </span>
        ) : null,
    },
  ];

  const historyRows = history.data?.items ?? [];

  return (
    <section className="planning-screen" data-testid="planning-workbench">
      {pageHead}
      {control.banner}
      {failures > 0 ? (
        <Banner kind="bad" data-testid="planning-workbench-failures">
          {t("planning.workbench.failures", { count: failures })}
        </Banner>
      ) : null}
      {headData?.pending_request ? (
        <PendingLine request={headData.pending_request} format={time} />
      ) : null}
      {last !== undefined ? (
        <p
          className="planning-last"
          data-testid="planning-workbench-last"
          data-status={last.status}
        >
          <span>{t("planning.workbench.last", { time: time(last.started_at) })}</span>
          <TriggerChip trigger={last.trigger} />
          <RefreshStatusChip status={last.status} />
          {last.status === "failed" && canRefresh ? (
            <Button
              size="sm"
              data-testid="planning-workbench-rerun"
              disabled={control.running || offline}
              onClick={control.start}
            >
              {t("planning.workbench.rerun")}
            </Button>
          ) : null}
        </p>
      ) : null}
      {neverRun ? (
        <EmptyState
          title={t("planning.workbench.emptyTitle")}
          body={t("planning.workbench.emptyBody")}
        />
      ) : (
        <>
          <DemandTiles
            latest={latest}
            subtotals={pool.data?.subtotals}
            loading={pool.isPending}
            mps={<MpsTile facilityId={facility.id} timezone={zone} />}
          />
          <LatestLines detail={detail.data} />
          <Card title={t("planning.workbench.historyTitle")}>
            <DataTable
              columns={columns}
              rows={historyRows}
              rowKey={(r) => r.id}
              rowTestId="planning-refresh-row"
              rowClassName={(r) => (r.status === "failed" ? "planning-row-failed" : undefined)}
              loading={history.isPending}
              loadingLabel={t("planning.workbench.loading")}
              caption={t("planning.workbench.historyTitle")}
              empty={
                <EmptyState
                  title={t("planning.workbench.emptyTitle")}
                  body={t("planning.workbench.emptyBody")}
                />
              }
              error={
                history.error !== null ? (
                  <ErrorState
                    title={t("planning.workbench.loadError")}
                    retryLabel={t("planning.common.retry")}
                    onRetry={() => void history.refetch()}
                    requestId={requestIdOf(history.error)}
                    requestIdLabel={t("planning.common.requestId")}
                  />
                ) : undefined
              }
              pager={{
                range: t("planning.common.page", { page: page + 1 }),
                page: page + 1,
                onPage: (n) => {
                  if (n > page + 1 && history.data?.next_cursor)
                    setCursors([...cursors, history.data.next_cursor]);
                  else if (n < page + 1) setCursors(cursors.slice(0, -1));
                },
                prevLabel: t("planning.common.previous"),
                nextLabel: t("planning.common.next"),
                hasPrev: page > 0,
                hasNext: (history.data?.next_cursor ?? null) !== null,
              }}
            />
          </Card>
        </>
      )}
      {perms.has("planning.planned_order.read") ? <MrpWorkbenchSection /> : null}
      {perms.has("planning.schedule.read") ? <ScheduleWorkbenchSection /> : null}
      {perms.has("planning.read") ? <PromisesWorkbenchSection /> : null}
    </section>
  );
}

function PendingLine({
  request,
  format,
}: {
  request: DemandPendingRequest;
  format: (iso: string | null) => string;
}) {
  const { t } = useTranslation();
  const retrying = request.failed_attempts > 0 && request.next_attempt_at !== null;
  return (
    <p
      className="planning-note"
      role="status"
      data-testid="planning-pending-request"
      data-count={request.trigger_count}
      data-due-at={request.due_at}
    >
      {t("planning.workbench.pending", {
        count: request.trigger_count,
        time: format(request.due_at),
      })}
      {retrying
        ? ` ${t("planning.workbench.retrying", {
            count: request.failed_attempts,
            time: format(request.next_attempt_at),
          })}`
        : null}
    </p>
  );
}

function DemandTiles({
  latest,
  subtotals,
  loading,
  mps,
}: {
  latest: DemandRefresh | null;
  subtotals: readonly { product_id: string; total: string }[] | undefined;
  loading: boolean;
  mps: ReactNode;
}) {
  const { t } = useTranslation();
  const rows = useMemo(
    () => (subtotals ?? []).map((s) => ({ product_id: s.product_id, qty: s.total })),
    [subtotals],
  );
  const products = useProductIndex(rows.map((r) => r.product_id));
  const uoms = useUoms();
  const unitCode = (id: string) => uoms.data?.items.find((u) => u.id === id)?.code;
  const { totals, unknown } = totalsByUnit(rows, products.byId);
  const settling = loading || products.isPending || uoms.isPending;
  const stats = latest?.stats;
  return (
    <StatStrip data-testid="planning-workbench-tiles">
      <StatTile
        label={t("planning.workbench.tiles.openDemand")}
        loading={settling}
        data-testid="planning-tile-open-demand"
        value={
          latest === null ? (
            "–"
          ) : totals.length === 0 ? (
            "0"
          ) : (
            <span className="planning-unit-totals">
              {totals.map((total) => (
                <QtyWithUnit
                  key={total.uomId}
                  qty={total.qty}
                  unit={unitCode(total.uomId)}
                  testId="planning-open-demand-total"
                />
              ))}
            </span>
          )
        }
        sub={
          latest === null
            ? t("planning.workbench.tiles.noRefresh")
            : unknown > 0
              ? t("planning.workbench.tiles.partial", { count: unknown })
              : t("planning.workbench.tiles.asOf")
        }
      />
      <StatTile
        label={t("planning.workbench.tiles.orderLines")}
        loading={loading}
        data-testid="planning-tile-order-lines"
        value={stats?.order_lines ?? 0}
      />
      <StatTile
        label={t("planning.workbench.tiles.deferred")}
        loading={loading}
        data-testid="planning-tile-deferred"
        value={stats?.credit_deferred?.count ?? 0}
        sub={t("planning.workbench.tiles.deferredSub")}
      />
      <StatTile
        label={t("planning.workbench.tiles.skipped")}
        loading={loading}
        data-testid="planning-tile-skipped"
        value={stats?.skipped_count ?? 0}
        sub={t("planning.workbench.tiles.skippedSub")}
      />
      {mps}
    </StatStrip>
  );
}

/** The rows each deferred or skipped list shows at once (rev-7 client pager, D-426). */
const LINE_PAGE = 50;

function LatestLines({ detail }: { detail: DemandRefreshDetail | undefined }) {
  const { t } = useTranslation();
  if (detail === undefined) return null;
  const labels = new Map(detail.line_labels.map((l) => [l.sales_order_line_id, l.label]));
  const deferred = detail.stats.credit_deferred;
  const skipped = detail.stats.skipped ?? [];
  const deferredIds = deferred?.sales_order_line_ids ?? [];
  if (deferredIds.length === 0 && skipped.length === 0) return null;
  // The refresh stores up to 200 ids of each list, so both page at 50 rows
  // (D-426): a card of 200 labels is a scroll, not a list. A new refresh
  // starts both on their first page.
  const pager = {
    pageSize: LINE_PAGE,
    resetKey: detail.id,
    range: (from: number, to: number, total: number) =>
      t("planning.common.rowsRange", { from, to, total }),
    prevLabel: t("planning.common.previous"),
    nextLabel: t("planning.common.next"),
  };
  type SkippedRow = (typeof skipped)[number] & { readonly key: string };
  const skippedRows: SkippedRow[] = skipped.map((line, i) => ({
    ...line,
    key: line.sales_order_line_id ?? line.shipment_line_id ?? String(i),
  }));
  const deferredColumns: DataTableColumn<{ readonly id: string }>[] = [
    {
      key: "line",
      header: t("planning.workbench.columns.orderLine"),
      id: true,
      render: (row) => <OrderLabel label={labels.get(row.id) ?? null} />,
    },
  ];
  const skippedColumns: DataTableColumn<SkippedRow>[] = [
    {
      key: "line",
      header: t("planning.workbench.columns.orderLine"),
      id: true,
      render: (line) =>
        line.source === "order_line" && line.sales_order_line_id !== null ? (
          <OrderLabel label={labels.get(line.sales_order_line_id) ?? null} />
        ) : (
          <span>{t("planning.workbench.shipmentLine")}</span>
        ),
    },
    {
      key: "why",
      header: t("planning.workbench.columns.why"),
      render: () => (
        <span className="planning-bad">{t("planning.workbench.uomNotConvertible")}</span>
      ),
    },
  ];
  return (
    <div className="planning-lines">
      {deferredIds.length > 0 ? (
        <Card title={t("planning.workbench.deferredTitle")} data-testid="planning-deferred-lines">
          <p className="planning-hint">
            {t("planning.workbench.deferredBody", { count: deferred?.count ?? deferredIds.length })}
          </p>
          <DataTable
            columns={deferredColumns}
            rows={deferredIds.map((id) => ({ id }))}
            rowKey={(row) => row.id}
            caption={t("planning.workbench.deferredTitle")}
            rowTestId="planning-deferred-line"
            rowProps={(row) => ({ "data-line-id": row.id })}
            clientPager={pager}
            empty={null}
          />
        </Card>
      ) : null}
      {skipped.length > 0 ? (
        <Card title={t("planning.workbench.skippedTitle")} data-testid="planning-skipped-lines">
          <p className="planning-hint">
            {t("planning.workbench.skippedBody", {
              count: detail.stats.skipped_count ?? skipped.length,
            })}
          </p>
          <DataTable
            columns={skippedColumns}
            rows={skippedRows}
            rowKey={(row) => row.key}
            caption={t("planning.workbench.skippedTitle")}
            rowTestId="planning-skipped-line"
            rowProps={(row) => ({ "data-source": row.source })}
            clientPager={pager}
            empty={null}
          />
        </Card>
      ) : null}
    </div>
  );
}
